"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";

const STORAGE_KEY = "assignowl_affiliate_ref";
const SESSION_VISIT_KEY = "assignowl_visit_tracked";
const TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

export interface StoredRef {
  ref: string;
  savedAt: number;
}

/** Save a ref to localStorage with a timestamp. */
function saveRef(ref: string) {
  const entry: StoredRef = { ref, savedAt: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
}

/**
 * Read the stored affiliate ref if it exists and is still within the TTL.
 * Returns the ref string, or null if missing / expired.
 */
export function getStoredRef(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { ref, savedAt }: StoredRef = JSON.parse(raw);
    if (Date.now() - savedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return ref;
  } catch {
    return null;
  }
}

/** Clear the stored ref (call after a successful signup). */
export function clearStoredRef() {
  if (typeof window !== "undefined") localStorage.removeItem(STORAGE_KEY);
}

/** Fire-and-forget visit ping — deduplicated server-side per IP per day. */
function pingVisit(ref: string) {
  const sessionKey = `${SESSION_VISIT_KEY}:${ref}`;
  if (sessionStorage.getItem(sessionKey)) return; // already sent this session
  sessionStorage.setItem(sessionKey, "1");

  fetch(`${BASE_URL}/public/affiliate-visit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ref }),
  }).catch(() => {}); // silently ignore network errors
}

/**
 * Drop this anywhere in the layout tree (inside a Suspense boundary).
 * It captures ?ref=AFF-* from any page URL, persists it, and pings the
 * visit-tracking API once per session per ref code.
 */
function RefCaptureInner() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const ref = searchParams?.get("ref");
    if (ref?.toUpperCase().startsWith("AFF-")) {
      saveRef(ref);
      pingVisit(ref);
    }
  }, [searchParams]);

  return null;
}

export function RefCaptureProvider() {
  return (
    <RefCaptureInner />
  );
}
